"use client";

import React, { useCallback, useEffect, useMemo, memo, useState } from 'react';
import Image from 'next/image';

interface AppImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  quality?: number;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  fill?: boolean;
  sizes?: string;
  onClick?: () => void;
  fallbackSrc?: string;
  loading?: 'lazy' | 'eager';
  unoptimized?: boolean;
  [key: string]: any;
}

const AppImage = memo(function AppImage({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  quality = 85,
  placeholder = 'empty',
  blurDataURL,
  fill = false,
  sizes,
  onClick,
  fallbackSrc = '/assets/images/no_image.png',
  loading = 'lazy',
  unoptimized = false,
  ...props
}: AppImageProps) {
  const [imageSrc, setImageSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setImageSrc(src);
    setIsLoading(true);
    setHasError(false);
  }, [src]);

  const isExternalUrl = useMemo(
    () => typeof imageSrc === 'string' && imageSrc.startsWith('http'),
    [imageSrc]
  );
  const resolvedUnoptimized = unoptimized || isExternalUrl;

  const handleError = useCallback(() => {
    if (!hasError && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      setHasError(true);
    }
    setIsLoading(false);
  }, [hasError, imageSrc, fallbackSrc]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const imageClassName = useMemo(() => {
    const classes = [className];
    if (isLoading) classes.push('bg-gray-200');
    if (onClick) classes.push('cursor-pointer hover:opacity-90 transition-opacity duration-200');
    return classes.filter(Boolean).join(' ');
  }, [className, isLoading, onClick]);

  const imageProps = useMemo(() => {
    const baseProps: any = {
      src: imageSrc,
      className: imageClassName,
      quality,
      placeholder,
      unoptimized: resolvedUnoptimized,
      onError: handleError,
      onLoad: handleLoad,
      onClick,
    };

    if (priority) {
      baseProps.priority = true;
    } else {
      baseProps.loading = loading;
    }

    if (blurDataURL && placeholder === 'blur') {
      baseProps.blurDataURL = blurDataURL;
    }

    return baseProps;
  }, [
    imageSrc,
    imageClassName,
    quality,
    placeholder,
    blurDataURL,
    resolvedUnoptimized,
    priority,
    loading,
    handleError,
    handleLoad,
    onClick,
  ]);

  if (fill) {
    const styleFromProps = (props as any)?.style as React.CSSProperties | undefined;
    const resolvedObjectFit =
      styleFromProps?.objectFit ||
      (imageClassName.includes('object-contain') ? 'contain' : 'cover');
    const mergedStyle: React.CSSProperties = {
      ...styleFromProps,
      objectFit: resolvedObjectFit,
    };
    const { style: _style, ...restProps } = props as any;

    return (
      <div data-component-id="src/components/ui/AppImage.tsx:128:6" data-component-path="src/components/ui/AppImage.tsx" data-component-line="128" data-component-end-line="137" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative" style={{ width: '100%', height: '100%' }}>
        <Image data-component-id="src/components/ui/AppImage.tsx:129:8" data-component-path="src/components/ui/AppImage.tsx" data-component-line="129" data-component-end-line="136" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22%5Bspread%5D%22%3A%22true%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%7D"
          {...imageProps}
          alt={alt}
          fill
          sizes={sizes || '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'}
          style={mergedStyle}
          {...restProps}
        />
      </div>
    );
  }

  return (
    <Image data-component-id="src/components/ui/AppImage.tsx:142:4" data-component-path="src/components/ui/AppImage.tsx" data-component-line="142" data-component-end-line="149" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22%5Bspread%5D%22%3A%22true%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%7D"
      {...imageProps}
      alt={alt}
      width={width || 400}
      height={height || 300}
      sizes={sizes}
      {...props}
    />
  );
});

AppImage.displayName = 'AppImage';

export default AppImage;
